import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Card } from "@/components/ui/card"
import SubscriptionSettings from "./SubscriptionSettings"
import BillingSettings from "./BillingSettings"
import TeamManagement from "./TeamManagement"
import { usePlanLimitations } from "@/hooks/usePlanLimitations"
import { PlanUpgradePrompt } from "@/components/ui/plan-upgrade-prompt"
import { useState, useEffect } from "react"

export default function SettingsPage() {
    // Check URL hash to maintain tab state
    const getInitialTab = () => {
        const hash = window.location.hash.replace('#', '')
        return ['subscription', 'billing', 'team'].includes(hash) ? hash : 'subscription'
    }
    
    const [activeTab, setActiveTab] = useState(getInitialTab())
    const { isFeatureAvailable, loading } = usePlanLimitations()
    const hasTeamsFeature = !loading && isFeatureAvailable("teams")
    
    // Update URL hash when tab changes
    const handleTabChange = (value: string) => {
        setActiveTab(value)
        window.location.hash = value
    }
    
    // Listen for hash changes (browser back/forward)
    useEffect(() => {
        const handleHashChange = () => {
            const hash = window.location.hash.replace('#', '')
            if (['subscription', 'billing', 'team'].includes(hash)) {
                setActiveTab(hash)
            }
        }
        
        window.addEventListener('hashchange', handleHashChange)
        return () => window.removeEventListener('hashchange', handleHashChange)
    }, [])

    return (
        <div className="container p-6">
            <div className="mb-6">
                <h1 className="text-3xl font-bold">Subscription & Billing Settings</h1>
                <p className="text-muted-foreground">Manage your subscription, billing, and team settings</p>
            </div>

            <Tabs 
                value={activeTab} 
                onValueChange={handleTabChange}
                className="space-y-4"
            >
                <TabsList>
                    <TabsTrigger value="subscription">Subscription</TabsTrigger>
                    <TabsTrigger value="billing">Billing</TabsTrigger>
                </TabsList>

                <TabsContent value="subscription">
                    <Card className="p-6">
                        <SubscriptionSettings />
                    </Card>
                </TabsContent>

                <TabsContent value="billing">
                    <Card className="p-6">
                        <BillingSettings />
                    </Card>
                </TabsContent>
                
                <TabsContent value="team">
                    <Card className="p-6">
                        {hasTeamsFeature ? (
                            <TeamManagement />
                        ) : (
                            <PlanUpgradePrompt 
                                featureName="Team Management" 
                                description="The Team Management feature allows you to invite team members to collaborate on your brands and campaigns. Upgrade your plan to access this feature."
                            />
                        )}
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    )
} 